/**
 * Previous / next item without going back to the list (case round,
 * finding 6: Jess opened 30 items in a row, back to the list each time).
 *
 * A list tab opens an item with the numbers of the items it shows, in the
 * order it shows them (`state.order`); the workspace walks that list. A
 * pasted link carries no list, so it shows no previous / next.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */

export interface ItemStep {
  /** "3 of 12" */
  position: string;
  /** Where this item sits in the list, 0-based (recorded with a step). */
  index: number;
  /** This item's number as the list named it (a case opened as its
   *  thread is in the list under one of its two numbers). */
  current: string;
  prev: string | null;
  next: string | null;
}

export type StepDirection = 'prev' | 'next';

/** The recorded action name for a step: qcp_prev / qcp_next, the door's
 *  twin of the inbox's email_prev / email_next, so the usage graph can tell
 *  a step from a fresh open of the next item's URL. */
export function stepActionName(direction: StepDirection): `qcp_${StepDirection}` {
  return `qcp_${direction}`;
}

/** The recorder's data-target-* key for an item number: a QT thread or an
 *  INV case, so the row names what it stepped to. */
export function itemTargetAttr(no: string): 'data-target-case-id' | 'data-target-thread-id' {
  return /^INV-/i.test(no) ? 'data-target-case-id' : 'data-target-thread-id';
}

/** The item order a list passed, when there is one. */
export function orderFromState(state: unknown): string[] | null {
  const order = (state as { order?: unknown } | null)?.order;
  if (!Array.isArray(order) || order.length === 0) return null;
  return order.every((n) => typeof n === 'string') ? (order as string[]) : null;
}

/** Where `current` (QT-… / INV-…) sits in `order`; null when it is not in it. */
export function itemStep(order: string[] | null, current: string[]): ItemStep | null {
  if (!order) return null;
  const upper = current.map((c) => c.toUpperCase());
  const i = order.findIndex((n) => upper.includes(n.toUpperCase()));
  if (i < 0) return null;
  return {
    position: `${i + 1} of ${order.length}`,
    index: i,
    current: order[i],
    prev: i > 0 ? order[i - 1] : null,
    next: i < order.length - 1 ? order[i + 1] : null,
  };
}

/** The workspace path for an item number, from the workspace route. */
export function itemPathFromWorkspace(no: string): string {
  const kind = /^INV-/i.test(no) ? 'cases' : 'threads';
  return `../${kind}/${encodeURIComponent(no)}`;
}
